import { 
  getAuth, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { app, db } from '@infrastructure/firebase';

// Initialize auth with explicit app reference
const auth = getAuth(app);

// Debug auth object
console.log("🔍 DEBUG: Auth object initialized with:", {
  app: app ? "Firebase app exists" : "No Firebase app",
  appConfig: app?.options ? {
    apiKey: app.options.apiKey ? "API key exists (starts with: " + app.options.apiKey.substring(0, 5) + "...)" : "No API key",
    projectId: app.options.projectId,
    authDomain: app.options.authDomain
  } : "No app options"
});

// User roles and permissions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  PRODUCT_ADMIN: 'product_admin',
  EDITOR: 'editor'
};

export const authService = {
  // Sign in with email and password
  async signIn(email, password) {
    try {
      console.log('Attempting to sign in with:', email);
      console.log('Using auth object with app config:', auth.app.options.apiKey ? 
                  "API key exists (starts with: " + auth.app.options.apiKey.substring(0, 5) + "...)" : "No API key", 
                  "Project ID:", auth.app.options.projectId);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Sign in successful, user:', userCredential.user.uid);
      const userPermissions = await this.getUserPermissions(userCredential.user.uid);
      console.log('User permissions:', userPermissions);
      return {
        user: userCredential.user,
        permissions: userPermissions
      };
    } catch (error) {
      console.error('Sign in error:', error.code, error.message);
      throw error;
    }
  },

  // Sign out
  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  // Get current user with permissions
  getCurrentUser() {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, 
        async (user) => {
          unsubscribe();
          if (user) {
            console.log('Current user found:', user.uid);
            const permissions = await this.getUserPermissions(user.uid);
            console.log('Current user permissions:', permissions);
            resolve({ user, permissions });
          } else {
            console.log('No current user found');
            resolve(null);
          }
        },
        reject
      );
    });
  },

  // Subscribe to auth state changes
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get user permissions from Firestore
  async getUserPermissions(userId) {
    try {
      console.log('Fetching permissions for user:', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        console.log('No user document found in Firestore');
        return null;
      }
      
      const userData = userDoc.data();
      console.log('User data from Firestore:', userData);
      return {
        role: userData.role,
        products: userData.products || {},
        isSuperAdmin: userData.role === ROLES.SUPER_ADMIN
      };
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return null;
    }
  },

  // Check if user has access to a specific product
  hasProductAccess(permissions, productId) {
    if (!permissions) return false;
    if (permissions.isSuperAdmin) return true;
    return permissions.products[productId] === true;
  }
}; 